import { Dialog, Listbox, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { votersAPI } from "../../../services/api";
import toast from "react-hot-toast";
import { validateVoter } from "../../../validation/voters/voter";
import { useMutation, useQueryClient } from "react-query";

export default function AddVoterModal({ isOpen, onClose, onVoterAdded }) {
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    address: "",
    precinct: "",
    pwd: false,
    senior: false,
    illiterate: false,
    residentId: null,
    voters_id_no: "",
    registration_date: "",
  });

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [precincts, setPrecincts] = useState([]);
  const [errors, setErrors] = useState({});

  const queryClient = useQueryClient();

  const createVoterMutation = useMutation(
    (payload) => votersAPI.createVoter(payload),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries("voters");
        if (typeof onVoterAdded === "function") {
          try {
            await onVoterAdded();
          } catch (e) {
            // swallow refetch errors to not block closing modal
            console.error("onVoterAdded failed", e);
          }
        }
        onClose();
      },
    }
  );

  //  Fetch precincts 
  useEffect(() => {
    const fetchPrecincts = async () => {
      try {
        const res = await votersAPI.getPrecincts({ per_page: 100 });
        if (res.data?.success) {
          const list = res.data.data.precincts || res.data.data || [];
          setPrecincts(list);
        } else {
          console.warn("Failed to fetch precincts:", res);
        }
      } catch (err) {
        console.error("Error fetching precincts:", err);
      }
    };
    fetchPrecincts();
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: "",
        dob: "",
        address: "",
        precinct: "",
        pwd: false,
        senior: false,
        illiterate: false,
        residentId: null,
        voters_id_no: "",
        registration_date: "",
      });
      setSearchResults([]);
    }
  }, [isOpen]);

  // Search residents 
  const handleSearch = async (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, name: value }));

    if (value.length < 1) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const res = await votersAPI.searchResident(value);
      console.log("API Response:", res.data);

      const residentData = res.data?.data?.searched_resident;

      if (Array.isArray(residentData)) {
        setSearchResults(residentData);
      } else if (residentData && typeof residentData === "object") {
        setSearchResults([residentData]);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("Error fetching resident:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  //  Select a resident from search results autofill
  const handleSelectVoter = (resident) => {
    let formattedDOB = "";
    if (resident.formatted_birthdate) {
      const [month, day, year] = resident.formatted_birthdate.split("-");
      formattedDOB = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // Find matching precinct by precinct_id or precinct_name
    let selectedPrecinct = "";
    if (resident.precinct_id || resident.precinct_name) {
      const matchingPrecinct = precincts.find(
        (p) =>
          (resident.precinct_id && (p.id === resident.precinct_id || p.precinct_id === resident.precinct_id)) ||
          (resident.precinct_name && (p.name === resident.precinct_name || p.precinct_name === resident.precinct_name))
      );
      if (matchingPrecinct) {
        selectedPrecinct = matchingPrecinct.id || matchingPrecinct.precinct_id;
      }
    }

    setFormData({
      name: resident.full_name || `${resident.first_name} ${resident.last_name}`,
      dob: formattedDOB || "",
      address: resident.full_address || "",
      precinct: selectedPrecinct,
      pwd: !!resident.pwd_status,
      senior: !!resident.senior_citizen_status,
      illiterate: !!resident.illiterate_status,
      residentId: resident.id || resident.resident_id || null,
      voters_id_no: resident.voters_id_no || "",
      registration_date: "",
    });
    setSearchResults([]);
    setErrors((prev) => ({ ...prev, name: undefined, dob: undefined, address: undefined }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await validateVoter(formData);
    if (!result.valid) {
      setErrors(result.errors || {});
      toast.error("Please fix the highlighted errors");
      return;
    }
    const payload = {
      resident_id: formData.residentId,
      precinct_id: formData.precinct,
      registration_date: formData.registration_date || new Date().toISOString(),
      voters_id_no: formData.voters_id_no || undefined,
      status: "active",
      pwd_status: !!formData.pwd,
      senior_citizen_status: !!formData.senior,
      illiterate_status: !!formData.illiterate,
    };

    try {
      await toast.promise(
        createVoterMutation.mutateAsync(payload),
        {
          loading: "Adding voter...",
          success: "Voter created successfully",
          error: (err) => err?.response?.data?.message || "Failed to create voter",
        }
      );
    } catch (err) {
      const backendErrors = err?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === "object") {
        const mapped = {};
        Object.keys(backendErrors).forEach((k) => {
          const val = backendErrors[k];
          mapped[k] = Array.isArray(val) ? val[0] : String(val);
        });
        setErrors((prev) => ({ ...prev, ...mapped }));
      }
    }
  };

  // Map precincts for display
  const precinctOptions = precincts.map((p) => ({
    label: p.name || p.precinct_name,
    value: p.id || p.precinct_id,
  }));

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <Dialog.Title className="text-xl font-bold text-gray-900">
              Add Resident as Voter
            </Dialog.Title>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
            >
              <XMarkIcon className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <div className="-mx-6 mb-4">
            <hr className="border-gray-300" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            {/* Full Name with Search */}
            <div className="relative">
              <label className="block text-base font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleSearch}
                  placeholder="Search for voter by name"
                  autoComplete="off"
                  className="w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base pl-10 pr-4 py-2.5"
                  required
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
              {/* Search Results Dropdown */}
              {!isSearching && searchResults.length > 0 && (
                <ul className="absolute z-10 bg-white border rounded-md mt-1 w-full max-h-40 overflow-y-auto shadow">
                  {searchResults.map((resident, idx) => (
                    <li
                      key={idx}
                      onClick={() => handleSelectVoter(resident)}
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                    >
                      {resident.full_name ||
                        `${resident.first_name} ${resident.last_name}`}
                    </li>
                  ))}
                </ul>
              )}
              {isSearching && (
                <div className="absolute mt-1 text-gray-400 text-sm px-2">
                  Searching...
                </div>
              )}
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-2.5"
              />
              {errors.dob && (
                <p className="mt-1 text-sm text-red-600">{errors.dob}</p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="House No., Street, Barangay, City, Province"
                className="w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-2.5"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            {/* Dynamic Precinct Dropdown */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-1">
                Precinct Assignment
              </label>
              <Listbox
                value={formData.precinct}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, precinct: val }))
                }
              >
                {({ open }) => (
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-800 sm:text-base">
                      <span className="block truncate">
                        {formData.precinct
                          ? precinctOptions.find(
                              (p) => p.value === formData.precinct
                            )?.label || "Select Precinct"
                          : "Select Precinct"}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        {open ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-400 transition-transform duration-200" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-400 transition-transform duration-200" />
                        )}
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100 scale-100"
                      leaveTo="opacity-0 scale-95"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/10 focus:outline-none sm:text-base">
                        {precinctOptions.length > 0 ? (
                          precinctOptions.map((p, idx) => (
                            <Listbox.Option
                              key={idx}
                              value={p.value}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                  active
                                    ? "bg-blue-100 text-blue-900"
                                    : "text-gray-900"
                                }`
                              }
                            >
                              {({ selected }) => (
                                <>
                                  <span
                                    className={`block truncate ${
                                      selected
                                        ? "font-semibold"
                                        : "font-normal"
                                    }`}
                                  >
                                    {p.label}
                                  </span>
                                  {selected && (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                      <CheckIcon className="h-5 w-5" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Listbox.Option>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-gray-500 text-sm">
                            No precincts found.
                          </div>
                        )}
                      </Listbox.Options>
                    </Transition>
                  </div>
                )}
              </Listbox>
              {errors.precinct && (
                <p className="mt-1 text-sm text-red-600">{errors.precinct}</p>
              )}
            </div>

            {/* Voter ID No */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-1">
                Voter ID No
              </label>
              <input
                type="text"
                name="voters_id_no"
                value={formData.voters_id_no}
                onChange={handleChange}
                placeholder="Enter voter ID number"
                className="w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-2.5"
              />
              {errors.voters_id_no && (
                <p className="mt-1 text-sm text-red-600">{errors.voters_id_no}</p>
              )}
            </div>

            {/* Registration Date */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-1">
                Registration Date
              </label>
              <input
                type="date"
                name="registration_date"
                value={formData.registration_date}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-2.5"
              />
              {errors.registration_date && (
                <p className="mt-1 text-sm text-red-600">{errors.registration_date}</p>
              )}
            </div>

            {/* Checkboxes */}
            <div className="col-span-2 grid grid-cols-3 gap-4 mt-2">
              <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  name="pwd"
                  checked={formData.pwd}
                  onChange={handleChange}
                  className="h-5 w-5 rounded border-gray-400"
                />
                <span className="text-base">Person with Disability (PWD)</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  name="senior"
                  checked={formData.senior}
                  onChange={handleChange}
                  className="h-5 w-5 rounded border-gray-400"
                />
                <span className="text-base">Senior Citizen</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  name="illiterate"
                  checked={formData.illiterate}
                  onChange={handleChange}
                  className="h-5 w-5 rounded border-gray-400"
                />
                <span className="text-base">Illiterate</span>
              </label>
            </div>

            <div className="col-span-2 -mx-6 mt-6 mb-4">
              <hr className="border-gray-300" />
            </div>

            {/* Buttons */}
            <div className="col-span-2 flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-md text-base hover:bg-gray-200 border border-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createVoterMutation.isLoading}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-md text-base hover:bg-blue-700 border border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {createVoterMutation.isLoading ? "Adding..." : "Add"}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
